/*  ==========================================================================
    Aurora Expandable Grid

    Drives the responsive behaviour of the "Expandable Grid" section
    (sections/expandable-grid.liquid).

    - Mobile: the native <details> accordion does all the work — every panel
      starts closed and any number can be opened. No JS required.
    - Desktop: panels must all render expanded as a static grid. CSS can't
      reliably force a closed <details> open across browsers, so this module
      toggles the `open` attribute at the grid breakpoint.

    Progressive enhancement: the desktop CSS that makes the <summary> inert and
    hides the toggle is gated behind a `data-eg-ready` attribute this module
    stamps on each root. Without JS the desktop therefore degrades to a working
    accordion (clickable summaries, visible toggles) rather than dead, collapsed
    panels. The server-rendered default stays closed, so mobile — the profile
    Core Web Vitals weights most — never shifts on load.

    A single page-level matchMedia listener drives every instance on the page,
    and it re-syncs on `shopify:section:load` so the theme editor stays correct.
============================================================================== */

const AuroraExpandableGrid = (function () {
  const DESKTOP_MQ = "(min-width: 768px)"; // must match the grid breakpoint in the section CSS
  const ITEM_SELECTOR = ".aur-expandable-grid__item";
  const SUMMARY_SELECTOR = ".aur-expandable-grid__summary";
  const ROOT_SELECTOR = "[data-expandable-grid]";

  let mql = null;

  /**
   * Force every panel in a single grid open (desktop) or closed (mobile).
   *
   * On desktop the panels are permanently expanded as a static grid, so the
   * <summary> is not a real disclosure control. CSS already blocks the mouse
   * (pointer-events: none); keyboard activation is neutralised by
   * blockDesktopToggle rather than a tabindex, so the <summary> keeps its
   * native semantics and Chrome's a11y audit isn't tripped by a tabindex'd
   * summary. On mobile the native accordion is left untouched.
   *
   * @param {Element} root  the [data-expandable-grid] container
   * @param {boolean} isDesktop
   * @return {void}
   */
  function syncSection(root, isDesktop) {
    // Signal that JS is now managing this grid so the inert-summary / hidden-
    // toggle desktop styles activate. Harmless on mobile (those rules live
    // inside the desktop media query).
    root.setAttribute("data-eg-ready", "");

    root.querySelectorAll(ITEM_SELECTOR).forEach((item) => {
      item.open = isDesktop;
    });
  }

  /**
   * Cancel <summary> activation on desktop so keyboard Enter/Space (or any
   * click that reaches it) can't collapse an always-open panel — there'd be no
   * re-sync until the next resize. Preventing the click's default action
   * cancels the native <details> toggle. Mobile is left alone so the accordion
   * works normally. Delegated at document level; bound once in init().
   *
   * @param {Event} event
   * @return {void}
   */
  function blockDesktopToggle(event) {
    const isDesktop = mql ? mql.matches : true;
    if (!isDesktop) {
      return;
    }

    const target = event.target;
    if (!target || !target.closest) {
      return;
    }

    const summary = target.closest(SUMMARY_SELECTOR);
    if (summary && summary.closest(ROOT_SELECTOR)) {
      event.preventDefault();
    }
  }

  /**
   * Re-sync every grid on the page against the current viewport.
   *
   * @return {void}
   */
  function syncAll() {
    const isDesktop = mql ? mql.matches : true;
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => syncSection(root, isDesktop));
  }

  /**
   * Bind the breakpoint listener once and run an initial sync. Safe to call
   * repeatedly — the listener is only attached on the first call.
   *
   * @return {void}
   */
  function init() {
    if (!("matchMedia" in window)) {
      return;
    }

    if (!mql) {
      mql = window.matchMedia(DESKTOP_MQ);
      mql.addEventListener("change", syncAll);
      document.addEventListener("shopify:section:load", syncAll);
      document.addEventListener("click", blockDesktopToggle);
    }

    syncAll();
  }

  return { init, syncAll };
})();

document.addEventListener("DOMContentLoaded", () => AuroraExpandableGrid.init());
